/** @jsxImportSource @emotion/react */

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { AppDispatch } from 'store/store';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { UDText } from 'ud-ui/components';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { UpsertLocationInput } from '../../domain/interfaces/actions';
import { fetchLocation, upsertLocation } from '../../store/actions';
import { selectLocation, selectLocations, selectProcess } from '../../store/selectors';
import LocationForm from '../components/location-form';
import useFetchMapImage from '../hooks/use-fetch-map-image';

function LocationViewPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { info } = useOrganizer();
  const tournamentId = useSelector(authStoreUserTournamentId);
  const locations = useSelector(selectLocations);
  const navigate = useNavigate();
  const location = useSelector(selectLocation(Number(useParams().id)));
  const process = useSelector(selectProcess);
  const { setHeaderContent } = useMainLayout();

  const id = Number(useParams().id);
  if (isNaN(id)) {
    navigate(Pages.LOCATIONS.INDEX);
  }

  const { isLoading: isMapImageLoading } = useFetchMapImage({ locationId: id });

  // Сначала ищем локацию в сторе
  const storedLocation = useMemo(() => locations.find((loc) => loc.id === id), [locations, id]);

  useEffect(() => {
    if (!storedLocation) {
      dispatch(fetchLocation({ id }));
    }
  }, [dispatch, id, storedLocation]);

  const handleSubmit = useCallback(
    async (values: UpsertLocationInput) => {
      if (!tournamentId) {
        console.error('No tournament selected');
        return;
      }

      try {
        const locationData: UpsertLocationInput = {
          ...values,
          tournamentId,
        };

        await dispatch(upsertLocation(locationData)).unwrap();
      } catch (error) {
        console.error('Failed to save location:', error);
      }
    },
    [dispatch, tournamentId],
  );

  const breadcrumbs = useMemo(() => {
    return [
      { label: 'Локации', to: Pages.LOCATIONS.INDEX },
      {
        label: location?.name ?? 'Неизвестная локация',
        to: Pages.LOCATIONS.builders.edit(id),
      },
    ];
  }, [id, location?.name]);

  const breadcrumb = useMemo(() => <UDBreadcrumbs className="my-10" breadcrumbs={breadcrumbs} />, [breadcrumbs]);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  if (!info || (process.isProcessing && process.name === 'fetchLocation')) {
    return (
      <>
        <div className="container pt-6">
          <div className="skeleton-block" style={{ height: '200px' }} />
        </div>
      </>
    );
  }

  if (!location) {
    return (
      <>
        <div className="container pt-6">
          <div className="flex-column flex-center mt-24">
            <div className="text body-text bold mb-2">Такая локация не найдена</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="container py-6">
        <UDText type="headline" className="mr-10">
          Локация для проведения турнира
        </UDText>
      </div>
      <div className="d-flex align-items-center bb-1 container" />
      <div className="container pt-6">
        <LocationForm
          location={location}
          isLoading={process.isProcessed && process.name === 'fetchLocation'}
          onSubmit={handleSubmit}
          action="edit"
          isAvailableFeatures={Boolean(info?.tournamentPlan)}
          isMapImageLoading={isMapImageLoading}
        />
      </div>
    </>
  );
}

export default LocationViewPage;
